/**
 * BerichtenPage — één communicatielijn tussen kantoor en uitvoering (field).
 *
 * Toont alle berichten: van uitvoering (field→office) die kantoor moet afhandelen,
 * én wat kantoor zelf naar uitvoering stuurde. Klik een bericht open → thread met
 * reacties + reageren + afhandelen. "Nieuw bericht" stuurt naar uitvoering.
 * WO/klant-namen openen inline de order- of klantdetail (bestaande drawer).
 */

import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { useBerichtenStore } from '../stores/berichten-store';
import {
  fetchBerichtList,
  fetchBerichtCounts,
  RICHTING_LABELS,
  type BerichtSummary,
  type Richting,
} from '../lib/berichten';
import { formatDateTime } from '../lib/format';
import {
  useBerichtenDrawerStore,
  parseDrawerParam,
  encodeDrawerParam,
} from '../stores/berichten-drawer-store';
import { BerichtenDrawerPanel } from '../components/berichten/BerichtenDrawerPanel';
import { BerichtStatusPill } from '../components/berichten/BerichtStatusPill';
import { BerichtConversation } from '../components/berichten/BerichtThread';
import { BerichtComposer } from '../components/berichten/BerichtComposer';

type RichtingFilter = 'field_to_office' | 'office_to_field' | 'reparateur' | 'all';
type StatusFilter = 'open' | 'afgehandeld' | 'all';

export function BerichtenPage() {
  const refreshBadge = useBerichtenStore((s) => s.refresh);

  const [berichten, setBerichten] = useState<BerichtSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [richting, setRichting] = useState<RichtingFilter>('field_to_office');
  const [status, setStatus] = useState<StatusFilter>('open');
  const [repOpen, setRepOpen] = useState(0);
  const [openThreadId, setOpenThreadId] = useState<string | null>(null);
  const [showCompose, setShowCompose] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const drawerOpenItem = useBerichtenDrawerStore((s) => s.openItem);
  const openDrawer = useBerichtenDrawerStore((s) => s.open);
  const closeDrawer = useBerichtenDrawerStore((s) => s.close);
  const isDrawerOpen = drawerOpenItem !== null;

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const rows = await fetchBerichtList({
        status: status === 'all' ? 'all' : status,
        ...(richting === 'reparateur'
          ? { peer: 'reparateur' as const }
          : richting === 'all'
            ? {}
            : { richting: richting as Richting }),
      });
      setBerichten(rows);
    } catch (err) {
      console.error('[BerichtenPage] load error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [status, richting]);

  // Open inkomende reparateur-berichten — voor de badge op de Reparateurs-tab.
  const loadRepCount = useCallback(async () => {
    try {
      const c = await fetchBerichtCounts('reparateur_to_office');
      setRepOpen(c.open);
    } catch {
      /* badge is best-effort */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadRepCount();
  }, [loadRepCount]);

  // URL → store sync (drawer voor order/klant detail)
  const openParam = searchParams.get('open');
  useEffect(() => {
    const parsed = parseDrawerParam(openParam);
    const current = useBerichtenDrawerStore.getState().openItem;
    const same = parsed && current && parsed.type === current.type && parsed.id === current.id;
    if (parsed && !same) openDrawer(parsed);
    else if (!parsed && current) closeDrawer();
  }, [openParam, openDrawer, closeDrawer]);

  // Store → URL sync
  useEffect(() => {
    const desired = encodeDrawerParam(drawerOpenItem);
    const current = searchParams.get('open');
    if (desired === current) return;
    const next = new URLSearchParams(searchParams);
    if (desired) next.set('open', desired);
    else next.delete('open');
    setSearchParams(next, { replace: true });
  }, [drawerOpenItem, searchParams, setSearchParams]);

  const richtingTabs: { key: RichtingFilter; label: string; badge?: number }[] = [
    { key: 'field_to_office', label: 'Van uitvoering' },
    { key: 'office_to_field', label: 'Naar uitvoering' },
    { key: 'reparateur', label: 'Reparateurs', badge: repOpen },
    { key: 'all', label: 'Alles' },
  ];
  const statusTabs: { key: StatusFilter; label: string }[] = [
    { key: 'open', label: 'Open' },
    { key: 'afgehandeld', label: 'Afgehandeld' },
    { key: 'all', label: 'Alles' },
  ];

  return (
    <>
      <div className="flex-1 flex overflow-hidden">
        <div
          className={clsx(
            'overflow-y-auto',
            isDrawerOpen ? 'basis-[40%] flex-shrink-0 min-w-0 p-6' : 'flex-1 p-6 max-w-4xl mx-auto'
          )}
        >
          {/* Header */}
          <div className="mb-6 flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-heading font-semibold text-main">Berichten</h1>
              <p className="text-sm text-muted mt-1">Communicatie met de uitvoering</p>
            </div>
            <button
              onClick={() => setShowCompose(true)}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 transition-colors shrink-0"
            >
              + Nieuw bericht
            </button>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-6">
            <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
              {richtingTabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setRichting(tab.key)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${
                    richting === tab.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {tab.label}
                  {tab.badge != null && tab.badge > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[11px] font-semibold bg-emerald-600 text-white">
                      {tab.badge}
                    </span>
                  )}
                </button>
              ))}
            </div>
            <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
              {statusTabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setStatus(tab.key)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    status === tab.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* List */}
          {isLoading ? (
            <div className="text-center py-12 text-muted">Laden…</div>
          ) : berichten.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted">Geen berichten gevonden</p>
            </div>
          ) : (
            <div className="space-y-3">
              {berichten.map((bericht) => (
                <BerichtCard
                  key={bericht.id}
                  bericht={bericht}
                  isOpen={openThreadId === bericht.id}
                  onToggle={() => setOpenThreadId((cur) => (cur === bericht.id ? null : bericht.id))}
                  onChanged={() => {
                    load();
                    refreshBadge();
                    loadRepCount();
                  }}
                  onOpenOrder={(id) => openDrawer({ type: 'order', id })}
                />
              ))}
            </div>
          )}
        </div>
        {isDrawerOpen && <BerichtenDrawerPanel />}
      </div>

      {showCompose && (
        <BerichtComposer
          onClose={() => setShowCompose(false)}
          onCreated={() => {
            setShowCompose(false);
            load();
            refreshBadge();
            loadRepCount();
          }}
        />
      )}
    </>
  );
}

interface CardProps {
  bericht: BerichtSummary;
  isOpen: boolean;
  onToggle: () => void;
  onChanged: () => void;
  onOpenOrder: (id: string) => void;
}

function BerichtCard({ bericht, isOpen, onToggle, onChanged, onOpenOrder }: CardProps) {
  const isOpenStatus = bericht.status === 'open';
  const isReparateur =
    bericht.richting === 'reparateur_to_office' || bericht.richting === 'office_to_reparateur';
  const inbound = bericht.richting === 'field_to_office' || bericht.richting === 'reparateur_to_office';
  const richtingClass = isReparateur
    ? 'bg-emerald-100 text-emerald-800'
    : inbound
      ? 'bg-amber-100 text-amber-800'
      : 'bg-indigo-100 text-indigo-700';

  return (
    <div
      className={clsx(
        'bg-white rounded-xl border transition-colors',
        isOpenStatus ? 'border-indigo-200' : 'border-slate-200'
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left p-4 hover:bg-slate-50/60 rounded-xl"
      >
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <BerichtStatusPill status={bericht.status} />
            <span
              className={clsx(
                'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                richtingClass
              )}
            >
              {RICHTING_LABELS[bericht.richting]}
            </span>
            {bericht.prioriteit === 'urgent' && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                Urgent
              </span>
            )}
          </div>
          <span className="text-xs text-slate-400 shrink-0">{formatDateTime(bericht.updatedAt)}</span>
        </div>

        <p className="text-sm font-medium text-slate-900 truncate">
          {bericht.onderwerp || bericht.laatsteReactiePreview || '(geen onderwerp)'}
        </p>

        {(bericht.werkOrderName ||
          bericht.customerName ||
          bericht.wasserijItemName ||
          bericht.reparatieOpdrachtNaam) && (
          <div className="mt-1 text-sm text-slate-600 flex items-center gap-1.5 flex-wrap">
            {bericht.reparatieOpdrachtNaam &&
              (bericht.werkOrderId ? (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenOrder(bericht.werkOrderId!);
                  }}
                  className="font-medium text-emerald-700 hover:underline cursor-pointer"
                  title="Open de reparatie-opdracht"
                >
                  {bericht.reparatieOpdrachtNaam}
                </span>
              ) : (
                <span className="font-medium text-emerald-700">{bericht.reparatieOpdrachtNaam}</span>
              ))}
            {bericht.werkOrderName && bericht.werkOrderId && (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenOrder(bericht.werkOrderId!);
                }}
                className="font-medium text-indigo-600 hover:underline cursor-pointer"
              >
                {bericht.reparatieOpdrachtNaam ? `· ${bericht.werkOrderName}` : bericht.werkOrderName}
              </span>
            )}
            {bericht.wasserijItemName && <span className="text-slate-500">· {bericht.wasserijItemName}</span>}
            {bericht.customerName && (
              <span className="text-slate-500">
                · {bericht.customerName}
              </span>
            )}
          </div>
        )}
      </button>

      {isOpen && (
        <div className="px-2 pb-2">
          <BerichtConversation berichtId={bericht.id} onChanged={onChanged} bordered={false} />
        </div>
      )}
    </div>
  );
}
